import { useRef, useState } from 'react';
import { ERROR_MESSAGE, INVALID_PROMO_CODE_MESSAGE } from 'appConstants';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import {
  applyCodeToSlots,
  droppedBySlotChange,
  PromoCodeSlots,
  removeCodeFromSlots,
} from 'subscriptionOptions/promoCodeSlots';
import { canApplyQuotePromoCode } from 'subscriptionOptions/quotePromoCodes';
import { subjectsCoveredInBasket } from 'subscriptionOptions/payNowPromoCodes';
import { PromoCodeBySubject } from 'subscriptionOptions/renewalPromoCodes';
import { GridSelection } from 'subscriptionOptions/types';

/**
 * The pay-now modal's own codes. Seeded once from the renewal codes: the modal mounts per opening,
 * so closing it discards every change and a reopen starts from the renewal codes again.
 */
export const usePayNowPromoCodes = (selection: GridSelection, renewalCodeBySubject: PromoCodeBySubject) => {
  const [slots, setSlots] = useState<PromoCodeSlots>(() => ({ ...renewalCodeBySubject }));
  const [dropped, setDropped] = useState<string[]>([]);
  // A remove can land while the check is in flight; placing onto the stale set would restore it.
  const latestSlots = useRef<PromoCodeSlots>(slots);
  latestSlots.current = slots;

  /** Resolves to an error message, or '' when the code was placed. */
  const applyCode = async (code: string): Promise<string> => {
    const trimmed = (code || '').trim();
    const held = Object.keys(slots).map((key) => slots[Number(key)]);
    const guard = canApplyQuotePromoCode(trimmed, held);

    if (!guard.allowed) return guard.error;

    // A read-only check, so the renewal codes stay as the panel set them.
    const result = await SubscriptionsApi.checkPromoCode(trimmed);

    if (result.error || !result.data) {
      return (result.error && result.error.message) || ERROR_MESSAGE;
    }

    const covered = subjectsCoveredInBasket(result.data, selection);

    if (covered.length === 0) return INVALID_PROMO_CODE_MESSAGE;

    const current = latestSlots.current;
    const next = applyCodeToSlots(current, trimmed, covered);

    latestSlots.current = next;
    setDropped(droppedBySlotChange(current, next));
    setSlots(next);
    return '';
  };

  const removeCode = (code: string) => {
    setDropped([]);
    latestSlots.current = removeCodeFromSlots(latestSlots.current, code);
    setSlots(latestSlots.current);
  };

  return { slots, dropped, applyCode, removeCode };
};
